import { IStoreState, PAY_DOCUMENT_STATUS } from '../types';
import { createSelector } from 'reselect';
import { find } from 'lodash';

export const getShop = (state: IStoreState) => state.shop;
export const getProductPrisingListSelector = createSelector(
  getShop,
  ({ pricingList }) => pricingList
);
export const getSelectedPriceListSelector = createSelector(
  getShop,
  ({ selectedPriceList }) => selectedPriceList
);
export const getPricingGroups = createSelector(getShop, ({ pricingGroups }) =>
  Array.isArray(pricingGroups) ? pricingGroups : []
);
export const getProductUserPricing = createSelector(getShop, ({ productUserPricing }) =>
  Array.isArray(productUserPricing) ? productUserPricing : []
);
export const getActiveDigitalProductUserPricing = createSelector(getShop, (list) => {
  const pricingGroups = Array.isArray(list.pricingGroups) ? list.pricingGroups : [];
  const productUserPricing = Array.isArray(list.productUserPricing) ? list.productUserPricing : [];
  const digitalLaboratoryGroup = pricingGroups.find((group) => group._productGroup === 'digital');
  if (digitalLaboratoryGroup && !digitalLaboratoryGroup.active) {
    return [];
  }

  return productUserPricing.filter(
    (pricing) => pricing._product?._productGroup?._id === 'digital' && pricing.active
  );
});
export const getUserAccountBalanceSelector = createSelector(
  getShop,
  ({ userBalance }) => userBalance
);
export const getProductPricingItemByIDSelector = createSelector(
  getProductPrisingListSelector,
  (list) => (_id: string) => find(list, { _id })
);
export const getShopCouponsSelector = createSelector(getShop, ({ coupons }) => coupons);
export const getShopCouponsFetchedSelector = createSelector(
  getShop,
  ({ isCouponsFetched }) => isCouponsFetched
);
export const getShopCouponByIDSelector = createSelector(
  getShopCouponsSelector,
  (coupons) => (id: string) => find(coupons, { id })
);
export const getProductGroupsList = createSelector(getShop, ({ productGroups }) => productGroups);
export const getProductList = createSelector(getShop, ({ products }) => products);
export const getGlobalShopSettingsSelector = createSelector(
  getShop,
  ({ globalSettings }) => globalSettings
);
export const getShopOrdersSelector = createSelector(getShop, ({ orders }) => orders);
export const getOrderImagesSelector = createSelector(getShop, ({ orderImages }) => orderImages);
export const getLaboratoriesSelector = createSelector(getShop, ({ laboratories }) => laboratories);
export const getOrderListById = createSelector(
  getShopOrdersSelector,
  ({ list }) =>
    (id: string) =>
      find(list, { id })
);
export const getPayDocuments = createSelector(getShop, ({ payDocuments }) => payDocuments);
export const getPayDocumentsNormalized = createSelector(getPayDocuments, (payDocuments) =>
  payDocuments.list.flatMap((payDocument) => {
    let creditNoteType = null;
    if (payDocument.creditNotes?.length > 0) {
      const refundedOrderIds = payDocument.creditNotes.flatMap((c) =>
        c.lines.map((l) => l.orderId)
      );
      const isFullRefund = payDocument.orderIds.every((id) => refundedOrderIds.includes(id));
      creditNoteType = isFullRefund ? 'Full' : 'Partial';
    }

    return [
      ...(payDocument.creditNotes || [])
        .map((creditNote) => ({
          date: creditNote.createdAt,
          url: creditNote.url,
          orderNumbers: creditNote.lines.map(
            (l) => payDocument.orderNumbers[payDocument.orderIds.indexOf(l.orderId)]
          ),
          reason: 'creditNote',
          status: PAY_DOCUMENT_STATUS.PAID,
          blocksAccountUntilPaid: null,
          creditNoteType: null
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
      {
        date: payDocument.date,
        url: payDocument.url,
        orderNumbers: payDocument.orderNumbers,
        reason: payDocument.reason,
        status: payDocument.status,
        blocksAccountUntilPaid: payDocument.blocksAccountUntilPaid,
        creditNoteType
      }
    ].flat();
  })
);
export const getUserHasEnoughInvoicingData = createSelector(
  getShop,
  ({ userHasEnoughInvoicingData }) => userHasEnoughInvoicingData
);
